// built-from: /chat-component v3 impl:chat-component role:implementation
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle, ChevronRight, Mic, Paperclip, Send, X } from 'lucide-react';
import type { ChatController, ConversationMessage } from '../hooks/sessionTypes';
import {
  ACCEPTED_FILE_ACCEPT_ATTR,
  validateAttachmentBatch,
} from '../utils/attachmentValidation';
import { partitionSessionAttachments } from '../utils/sessionAttachmentRouting';
import ImportFromDocumentModal from './ImportFromDocumentModal';
import '../styles/ArcPilotShell.css';

interface SessionCapturePanelProps {
  session: ChatController;
  className?: string;
  ariaLabel?: string;
  inputLabel?: string;
  emptyText?: string;
  // 100 (owner decision 1): there is NO host message renderer. Message
  // rendering — Markdown, GFM and `reply:` choices — belongs to this panel
  // alone; the old `renderMessage` escape hatch let ArcPilotShell's legacy
  // discovery routes drop reply choices on the floor. Hosts extend the reply
  // through renderAgentFooter below, never by replacing the message body.
  // 093: an optional footer under each facilitator reply, supplied by the host
  // (the companion offers Save as page and the Sources line there). A host that
  // passes nothing renders exactly as before - one panel, parameterised.
  renderAgentFooter?: (message: ConversationMessage, index: number) => React.ReactNode;
  dataState?: string;
  dataSpineRail?: boolean;
  dataWithDebug?: boolean;
  // Hide the voice (mic) control. The vision rail sets this: voice is wired to
  // the discovery realtime session and is not yet supported for the vision
  // context, so we don't show a button that can't work (honest UX) until it is.
  hideVoice?: boolean;
  /** Route every accepted file through the active facilitation bundle. */
  facilitationAttachments?: boolean;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMessageTimestamp = (timestamp: string) => {
  if (/^\d{4}-\d{2}-\d{2}T/.test(timestamp)) {
    const parsed = new Date(timestamp);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
  }
  const withSeconds = timestamp.match(/^(\d{1,2}:\d{2}):\d{2}(\s?[AP]M)?$/i);
  return withSeconds ? `${withSeconds[1]}${withSeconds[2] || ''}` : timestamp;
};

function replyText(href?: string): string | null {
  if (!href?.startsWith('reply:')) return null;
  try {
    return decodeURIComponent(href.slice('reply:'.length)).trim() || null;
  } catch {
    return null;
  }
}

function DefaultChatMessage({ text, choicesEnabled, choicesBusy, onChoose }: {
  text: string;
  choicesEnabled: boolean;
  choicesBusy: boolean;
  onChoose: (text: string) => void;
}) {
  return (
    <div className="ap-msg-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url) => (url.startsWith('reply:') ? url : defaultUrlTransform(url))}
        components={{
          a: ({ href, children }) => {
            const choice = replyText(href);
            if (!choice) return <a href={href}>{children}</a>;
            return choicesEnabled ? (
              <button type="button" className="ap-reply-choice" disabled={choicesBusy} onClick={() => onChoose(choice)}>
                {choice}
              </button>
            ) : <span>{children}</span>;
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

const SessionCapturePanel: React.FC<SessionCapturePanelProps> = ({
  session,
  className,
  ariaLabel = 'Capture chat',
  inputLabel = 'Message',
  emptyText = 'No messages yet. Start the conversation below.',
  renderAgentFooter,
  dataState,
  dataSpineRail,
  dataWithDebug,
  hideVoice = false,
  facilitationAttachments = false,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [inputDragOver, setInputDragOver] = useState(false);
  const [inputAttachmentErrors, setInputAttachmentErrors] = useState<string[]>([]);
  const [docImportQueue, setDocImportQueue] = useState<{ text: string; filename: string }[]>([]);

  const {
    conversationHistory,
    isLoading,
    inputBusy,
    callStatus,
    handleTextMessage,
    startVoiceCall,
    endVoiceCall,
    importFromDocument,
    onEscape,
    sessionError,
    activities,
  } = session;

  // 056 activity feedback (owner-agreed activity feedback, 2026-07-09): the
  // current turn's background activities. A failed entry renders nothing
  // (the row ends without noise); absent or empty renders exactly as today.
  const visibleActivities = (activities ?? []).filter((a) => a.status !== 'failed');

  useEffect(() => {
    chatEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [conversationHistory]);

  const queueDocImports = useCallback((docs: { file: File; text: string }[]) => {
    if (docs.length === 0) return;
    setDocImportQueue((prev) => [
      ...prev,
      ...docs.map((d) => ({ text: d.text, filename: d.file.name })),
    ]);
  }, []);

  const advanceDocImportQueue = useCallback(() => {
    setDocImportQueue((prev) => prev.slice(1));
  }, []);

  const handleSendFromInput = useCallback(async () => {
    const ta = inputRef.current;
    if (!ta || inputBusy) return;
    const text = ta.value.trim();
    if (!text && stagedFiles.length === 0) return;

    ta.value = '';
    ta.style.height = 'auto';
    const filesToSend = stagedFiles;
    setStagedFiles([]);
    setInputAttachmentErrors([]);

    if (facilitationAttachments) {
      await handleTextMessage(text, filesToSend);
    } else {
      const { structured, regular } = await partitionSessionAttachments(filesToSend);
      if (structured.length > 0) queueDocImports(structured);
      if (text || regular.length > 0) {
        await handleTextMessage(text, regular);
      }
    }
  }, [facilitationAttachments, handleTextMessage, inputBusy, queueDocImports, stagedFiles]);

  const addStagedFiles = useCallback((incoming: FileList | File[]) => {
    const { accepted, errors } = validateAttachmentBatch(incoming);
    if (accepted.length > 0) {
      setStagedFiles((prev) => [...prev, ...accepted]);
    }
    setInputAttachmentErrors(errors);
  }, []);

  const handleInputDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (isLoading) return;
    if (event.dataTransfer.types.includes('Files')) {
      event.preventDefault();
      setInputDragOver(true);
    }
  };

  const handleInputDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (event.currentTarget === event.target) setInputDragOver(false);
  };

  const handleInputDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setInputDragOver(false);
    if (isLoading) return;
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      addStagedFiles(event.dataTransfer.files);
    }
  };

  const currentDocImport = docImportQueue[0] ?? null;
  const panelClassName = ['ap-chat-panel', className].filter(Boolean).join(' ');
  let latestAgentIndex = -1;
  for (let index = conversationHistory.length - 1; index >= 0; index -= 1) {
    if (conversationHistory[index].type === 'agent') { latestAgentIndex = index; break; }
  }

  return (
    <>
      <div
        className={panelClassName}
        aria-label={ariaLabel}
        data-state={dataState}
        data-spine-rail={dataSpineRail ? 'true' : undefined}
        data-with-debug={dataWithDebug === undefined ? undefined : String(dataWithDebug)}
      >
        <div className="ap-messages">
          {sessionError ? (
            <div className="ap-msg ap-msg-agent" role="alert">
              <div className="ap-msg-content">
                <p>{sessionError}</p>
              </div>
            </div>
          ) : null}
          {conversationHistory.length === 0 && !sessionError && (
            <div className="ap-messages-empty">
              <p>{emptyText}</p>
            </div>
          )}
          {conversationHistory.map((msg, idx) => (
            <div key={msg.id ?? `${msg.type}-${idx}`} className={`ap-msg ap-msg-${msg.type}`}>
              <div className="ap-msg-content">
                {/* 100 (owner decision 8): the files a turn carried are part of
                    the turn. A turn that was nothing but a file shows the file,
                    live and after the conversation is reopened. */}
                {msg.attachments?.length ? (
                  <div className="ap-msg-atts">
                    {msg.attachments.map((attachment, attachmentIndex) => (
                      <span
                        className="ap-msg-att"
                        key={`${attachment.filename}-${attachmentIndex}`}
                      >
                        <Paperclip size={12} aria-hidden="true" />
                        <span className="ap-msg-att-name">{attachment.filename}</span>
                      </span>
                    ))}
                  </div>
                ) : null}
                {msg.text || !msg.attachments?.length ? (
                  <DefaultChatMessage
                    text={msg.text}
                    choicesEnabled={msg.type === 'agent' && idx === latestAgentIndex}
                    choicesBusy={inputBusy}
                    onChoose={(text) => { void handleTextMessage(text); }}
                  />
                ) : null}
                <small className="ap-msg-time">{formatMessageTimestamp(msg.timestamp)}</small>
                {msg.type === 'agent' && renderAgentFooter
                  ? renderAgentFooter(msg, idx)
                  : null}
              </div>
            </div>
          ))}
          {visibleActivities.length > 0 && (
            <div className="ap-activity-list">
              {visibleActivities.map((activity) =>
                activity.status === 'running' ? (
                  // The live row: a quiet spinner + plain label, deliberately
                  // distinct from the thinking dots below (this is background
                  // work on the turn, not the reply being composed).
                  <div key={activity.id} className="ap-activity ap-activity--running" role="status">
                    <span className="ap-activity-spinner" aria-hidden="true" />
                    <span className="ap-activity-label">{activity.label}</span>
                  </div>
                ) : (
                  // Settled: a collapsed disclosure in the house idiom. The
                  // expanded body carries what the work found, when there is
                  // anything to show.
                  <details key={activity.id} className="ap-activity ap-activity--done">
                    <summary className="ap-activity-summary">
                      <ChevronRight size={12} className="ap-activity-chevron" aria-hidden="true" />
                      <span className="ap-activity-label">{activity.label}</span>
                    </summary>
                    {activity.detail ? (
                      <p className="ap-activity-detail">{activity.detail}</p>
                    ) : null}
                  </details>
                ),
              )}
            </div>
          )}
          {callStatus === 'Connecting...' && (
            <div className="ap-msg ap-msg-agent ap-msg-typing">
              <div className="ap-msg-content">
                <span className="ap-voice-connecting">
                  <Mic size={14} className="ap-voice-connecting-icon" />
                  <span>Connecting voice...</span>
                </span>
              </div>
            </div>
          )}
          {isLoading && (
            <div className="ap-msg ap-msg-agent ap-msg-typing">
              <div className="ap-msg-content">
                <span className="ap-typing-dot" />
                <span className="ap-typing-dot" />
                <span className="ap-typing-dot" />
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div
          className={`ap-input-area${inputDragOver ? ' ap-input-area--drag' : ''}`}
          onDragOver={handleInputDragOver}
          onDragLeave={handleInputDragLeave}
          onDrop={handleInputDrop}
        >
          {inputAttachmentErrors.length > 0 && (
            <div className="ap-input-att-errors" role="alert">
              <AlertCircle size={14} />
              <div className="ap-input-att-errors-list">
                {inputAttachmentErrors.map((error, index) => (
                  <span key={`${error}-${index}`} className="ap-input-att-error">{error}</span>
                ))}
              </div>
              <button
                type="button"
                className="ap-input-att-errors-close"
                onClick={() => setInputAttachmentErrors([])}
                aria-label="Dismiss"
              >
                <X size={12} />
              </button>
            </div>
          )}
          {stagedFiles.length > 0 && (
            <div className="ap-input-attachments">
              {stagedFiles.map((file, index) => (
                <span key={`${file.name}-${index}`} className="ap-input-chip-att">
                  <Paperclip size={12} />
                  <span className="ap-input-chip-att-name" title={file.name}>{file.name}</span>
                  <span className="ap-input-chip-att-size">{formatFileSize(file.size)}</span>
                  <button
                    type="button"
                    className="ap-input-chip-att-remove"
                    onClick={() => setStagedFiles((prev) => prev.filter((_, idx) => idx !== index))}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <textarea
            ref={inputRef}
            aria-label={inputLabel}
            placeholder={inputDragOver ? 'Drop files to attach...' : 'Type your message...'}
            rows={1}
            className="ap-input-textarea"
            readOnly={inputBusy}
            onInput={(event) => {
              const textarea = event.currentTarget;
              textarea.style.height = 'auto';
              textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey && !inputBusy) {
                event.preventDefault();
                void handleSendFromInput();
              }
              if (event.key === 'Escape' && onEscape) {
                onEscape();
              }
            }}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPTED_FILE_ACCEPT_ATTR}
            onChange={(event) => {
              if (event.target.files) addStagedFiles(event.target.files);
              event.currentTarget.value = '';
            }}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="ap-input-attach"
            disabled={inputBusy}
            title="Attach files"
            aria-label="Attach files"
          >
            <Paperclip size={18} />
          </button>
          {!hideVoice && (
            <button
              type="button"
              onClick={() => {
                if (callStatus === 'On Call') endVoiceCall();
                else void startVoiceCall();
              }}
              className={`ap-input-mic ${callStatus === 'On Call' ? 'ap-input-mic--active' : ''}`}
              disabled={inputBusy || callStatus === 'Connecting...'}
              title={callStatus === 'On Call' ? 'End voice call' : 'Start voice call'}
              aria-label={callStatus === 'On Call' ? 'End voice call' : 'Start voice call'}
            >
              <Mic size={18} />
            </button>
          )}
          <button
            type="button"
            onClick={() => { void handleSendFromInput(); }}
            className="ap-input-send"
            disabled={inputBusy}
            aria-label="Send message"
          >
            <Send size={18} />
          </button>
        </div>
      </div>

      <ImportFromDocumentModal
        mode="into-session"
        isOpen={currentDocImport !== null}
        onClose={advanceDocImportQueue}
        presetContent={currentDocImport ?? undefined}
        onSubmit={async (text, { filename, force }) =>
          importFromDocument(text, { filename, force })
        }
      />
    </>
  );
};

export default SessionCapturePanel;
